import React, { useState, useEffect, useCallback } from 'react';
import { PieChart, Pie, Cell, Tooltip, ResponsiveContainer, BarChart, Bar, XAxis, YAxis, CartesianGrid, Legend } from 'recharts';
import { ChevronDown, Loader2, X, PlayCircle, PauseCircle, Phone, DollarSign, Clock, CheckCircle, AlertTriangle, PhoneOff } from 'lucide-react';

// --- TYPE DEFINITIONS (Based on API specs) ---
interface Collection {
    name: string;
}

interface CampaignSummary {
    call_summary: {
        total_calls: number;
        total_call_duration_sec: number;
        average_call_duration_sec: number;
        total_call_duration_formatted: string;
        total_cost_inr: number;
        average_call_duration_formatted: string;
        total_cost_usd: {
            telephony: number;
            stt: number;
            tts: number;
            llm: number;
            total: number;
        };
    };
    calling_status_counts: Record<string, number>;
    booking_status_counts: Record<string, number>;
}

interface CallDetail {
    user_data: {
        customer_name: string;
        phone_number: string;
        vehicle_name: string;
        license_plate: string;
        collection_name: string;
        calling_number: string;
    };
    call_sid: string;
    booking_status: string;
    call_details: {
        duration_formatted: string;
    };
    session_id: string; 
}

interface ChatLog {
    role: 'user' | 'assistant' | 'tool';
    content: string | null;
    tool_calls?: any[];
    tool_call_id?: string;
}

interface SessionData {
    customer_name: string;
    booking_status: string;
    session_id: string;
    chat_logs: ChatLog[];
    insights: {
        total_cost: { USD: number; INR: number; };
        open_ai_cost: { USD: number; INR: number; };
        cartesia_cost: { USD: number; INR: number; };
        deepgram_cost: { USD: number; INR: number; };
        twilio_cost: { USD: number; INR: number; };
        llm_usage: { prompt_tokens: number; completion_tokens: number; };
        call_details: { duration_formatted: string; };
        function_calls: { function_name: string; arguments: Record<string, any>; }[];
    };
    audio_url: string;
}


// --- API CONFIGURATION & FUNCTIONS ---
const BASE_URL = 'https://interim-cabdemo-module.ispgnet.com';

const api = {
    fetchCollections: async (): Promise<Collection[]> => {
        console.log("API: Fetching collections...");
        const response = await fetch(`${BASE_URL}/fetch_collections/`);
        if (!response.ok) throw new Error('Network response was not ok');
        const data = await response.json();
        return data.collections;
    },
    fetchCampaignSummary: async (collectionName: string, limit: number): Promise<CampaignSummary> => {
        console.log(`API: Fetching summary for ${collectionName} with limit ${limit}...`);
        const response = await fetch(`${BASE_URL}/campaign/${collectionName}/summary?limit=${limit}`);
        if (!response.ok) throw new Error('Network response was not ok');
        return response.json();
    },
    fetchCallDetails: async (collectionName: string, limit: number): Promise<CallDetail[]> => {
        console.log(`API: Fetching call details for ${collectionName} with limit ${limit}...`);
        const response = await fetch(`${BASE_URL}/campaign/${collectionName}?limit=${limit}`);
        if (!response.ok) throw new Error('Network response was not ok');
        return response.json();
    },
    fetchSessionData: async (sessionId: string): Promise<SessionData> => {
        console.log(`API: Fetching session data for ${sessionId}...`);
         const response = await fetch(`${BASE_URL}/chat/${sessionId}`);
        if (!response.ok) throw new Error('Network response was not ok');
        return response.json();
    }
};

// --- CHARTING COMPONENTS ---
const COLORS = ['#10b981', '#ef4444', '#f59e0b', '#4f46e5', '#8b5cf6'];

const CustomPieChart = ({ data, title }: { data: { name: string; value: number }[]; title: string }) => {
    const COLORS = ['#3B82F6', '#10B981', '#FBBF24', '#EF4444', '#8B5CF6', '#F472B6'];

    return (
        <div className="bg-white p-4 rounded-lg shadow-md h-full flex flex-col">
            <h3 className="font-semibold text-gray-600 text-center text-sm mb-3">{title}</h3>
            <div className="w-full h-64">
                <ResponsiveContainer width="100%" height="100%">
                    <PieChart>
                        <Pie
                            data={data}
                            cx="50%"
                            cy="50%"
                            innerRadius={60}
                            outerRadius={90}
                            paddingAngle={2}
                            labelLine={false}
                            label={({ name, percent }) => `${name} (${(percent * 100).toFixed(1)}%)`}
                            dataKey="value"
                            nameKey="name"
                            isAnimationActive={true}
                        >
                            {data.map((entry, index) => (
                                <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                            ))}
                        </Pie>
                        <Tooltip
                            content={({ active, payload }) => {
                                if (active && payload && payload.length) {
                                    const { name, value } = payload[0];
                                    const numericValue = typeof value === 'number' ? value : Number(value) || 0;

                                    return (
                                        <div className="bg-white border shadow rounded px-3 py-2 text-sm text-gray-700">
                                            <div className="font-semibold mb-1">{name}</div>
                                            <div><span className="text-gray-500">Count:</span> {numericValue.toFixed(2)}</div>
                                        </div>
                                    );
                                }
                                return null;
                            }}
                        />
                    </PieChart>
                </ResponsiveContainer>
            </div>
        </div>
    );
};

const CustomBarChart = ({ data, title, dataKey, nameKey }: { data: { name: string, value: number }[], title: string, dataKey: string, nameKey: string }) => (
    <div className="bg-white p-4 rounded-lg shadow-md h-full flex flex-col">
        <h3 className="text-lg font-bold text-gray-800 mb-4 text-center">{title}</h3>
        <div className="flex-grow">
            <ResponsiveContainer width="100%" height={300}>
                <BarChart data={data} margin={{ top: 5, right: 20, left: -10, bottom: 5 }}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#e0e0e0" />
                    <XAxis dataKey={nameKey} tick={{ fill: '#6b7280', fontSize: 12 }} />
                    <YAxis tick={{ fill: '#6b7280', fontSize: 12 }} />
                    <Tooltip
                        contentStyle={{ backgroundColor: '#ffffff', border: '1px solid #e5e7eb', borderRadius: '0.5rem' }}
                        itemStyle={{ color: '#1f2937' }}
                        cursor={{fill: 'rgba(239, 246, 255, 0.5)'}}
                    />
                    <Bar dataKey={dataKey} fill="#8884d8" name="Count">
                        {data.map((entry, index) => (
                            <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                        ))}
                    </Bar>
                </BarChart>
            </ResponsiveContainer>
        </div>
    </div>
);


// --- UI COMPONENTS ---
const SummaryCard = ({ title, value, icon, subValue }: { title: string; value: string | number; icon: React.ReactNode; subValue?: string }) => (
    <div className="bg-white p-6 rounded-lg shadow-md flex flex-col justify-between hover:shadow-lg transition-shadow duration-200">
        <div className="flex justify-between items-start">
            <p className="text-gray-500 text-sm font-medium">{title}</p>
            <div className="text-black-500">{icon}</div>
        </div>
        <div>
            <h2 className="text-2xl font-bold text-gray-900 mt-2">{value}</h2>
            {subValue && <p className="text-gray-500 text-xs">{subValue}</p>}
        </div>
    </div>
);

const CallingStatusCounts = ({ counts }: { counts: Record<string, number> }) => {
    const iconMap: { [key: string]: React.ReactNode } = {
        completed: <CheckCircle className="text-green-500" />,
        failed: <AlertTriangle className="text-red-500" />,
        busy: <PhoneOff className="text-yellow-500" />,
        };

    return (
        <div className="bg-white p-4 rounded-lg shadow-md">
            <h3 className="text-lg font-bold text-gray-800 mb-3">Calling Status Breakdown</h3>
            <div className="flex flex-wrap gap-4">
                {Object.entries(counts).map(([status, count]) => (
                    <div key={status} className="flex items-center gap-3 p-3 bg-gray-50 rounded-md">
                         {iconMap[status.toLowerCase()] || <Phone className="text-gray-500"/>}
                        <span className="text-gray-600 capitalize">{status}:</span>
                        <span className="font-bold text-lg text-gray-900">{count}</span>
                    </div>
                ))}
            </div>
        </div>
    );
};


const CostBreakdownCard = ({ costUsd }: { costUsd: CampaignSummary['call_summary']['total_cost_usd'] }) => (
     <div className="bg-white p-6 rounded-lg shadow-md hover:shadow-lg transition-shadow duration-200">
        <h3 className="text-lg font-bold text-gray-800 mb-4 flex items-center"><DollarSign className="w-5 h-5 mr-2 text-green-600"/>Cost Summary (USD)</h3>
        <div className="space-y-3 text-gray-600">
            {Object.entries(costUsd).map(([key, value]) => (
                 <div key={key} className="flex justify-between items-center text-sm">
                    <span className="capitalize font-medium text-gray-700">{key === 'stt' ? 'STT' : key === 'tts' ? 'TTS' : key === 'llm' ? 'LLM' : key}</span>
                     <span className={`font-semibold ${key === 'total' ? 'text-green-600 text-base' : 'text-gray-500'}`}>
                        ${typeof value === 'number' ? value.toFixed(2) : value}
                    </span>
                </div>
            ))}
        </div>
    </div>
)

const CollectionDropdown = ({ collections, selected, onSelect, isLoading }: { collections: Collection[], selected: string, onSelect: (name: string) => void, isLoading: boolean }) => {
    const [isOpen, setIsOpen] = useState(false);

    const handleSelect = (name: string) => {
        onSelect(name);
        setIsOpen(false);
    };

    return (
        <div className="relative inline-block text-left w-full sm:w-60">
            <button type="button" onClick={() => setIsOpen(!isOpen)} disabled={isLoading} className="inline-flex justify-between w-full rounded-md border border-gray-300 shadow-sm px-4 py-2 bg-white text-sm font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 disabled:opacity-50" id="options-menu" aria-haspopup="true" aria-expanded="true">
                {isLoading ? "Loading..." : selected || "Select Collection"}
                {isLoading ? <Loader2 className="animate-spin -mr-1 ml-2 h-5 w-5" /> : <ChevronDown className="-mr-1 ml-2 h-5 w-5" />}
            </button>

            {isOpen && (
                <div className="origin-top-right absolute right-0 mt-2 w-full rounded-md shadow-lg bg-white ring-1 ring-black ring-opacity-5 z-10">
                    <div className="py-1" role="menu" aria-orientation="vertical" aria-labelledby="options-menu">
                        {collections.map(collection => (
                             <a href="#" key={collection.name} onClick={(e) => { e.preventDefault(); handleSelect(collection.name); }} className="block px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 hover:text-gray-900" role="menuitem">{collection.name}</a>
                        ))}
                    </div>
                </div>
            )}
        </div>
    );
};

const CallDetailsTable = ({ calls, onSelectCall }: { calls: CallDetail[], onSelectCall: (session_id: string) => void }) => {
    if (!calls || calls.length === 0) {
        return (
            <div className="text-center py-10 bg-white rounded-lg shadow-md">
                <p className="text-gray-500">No call details to display.</p>
            </div>
        );
    }

    return (
        <div className="bg-white rounded-lg shadow-md overflow-hidden">
             <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-200">
                    <thead className="bg-gray-50">
                        <tr>
                            <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Customer</th>
                            <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Vehicle</th>
                            <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Booking Status</th>
                            <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Duration</th>
                            <th scope="col" className="relative px-6 py-3">
                                <span className="sr-only">View Session</span>
                            </th>
                        </tr>
                    </thead>
                    <tbody className="bg-white divide-y divide-gray-200">
                        {calls.map((call) => (
                            <tr key={call.call_sid} className="hover:bg-gray-50">
                                <td className="px-6 py-4 whitespace-nowrap">
                                    <div className="text-sm font-medium text-gray-900">{call.user_data.customer_name}</div>
                                    <div className="text-sm text-gray-500">{call.user_data.calling_number}</div>
                                </td>
                                <td className="px-6 py-4 whitespace-nowrap">
                                    <div className="text-sm text-gray-900">{call.user_data.vehicle_name}</div>
                                    <div className="text-sm text-gray-500">{call.user_data.license_plate}</div>
                                </td>
                                <td className="px-6 py-4 whitespace-nowrap">
                                    <span className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${
                                        call.booking_status === 'Booked' ? 'bg-green-100 text-green-800' :
                                        call.booking_status === 'Recall' ? 'bg-yellow-100 text-yellow-800' :
                                        call.booking_status === 'Pending' ? 'bg-blue-100 text-blue-800' :
                                        'bg-red-100 text-red-800'
                                    }`}>
                                        {call.booking_status}
                                    </span>
                                </td>
                                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-600">{call.call_details.duration_formatted}</td>
                                <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                                    <button onClick={() => onSelectCall(call.session_id)} className="text-blue-600 hover:text-blue-900 font-semibold">
                                        View Session
                                    </button>
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </table>
             </div>
        </div>
    );
};

const ChatMessage = ({ log }: { log: ChatLog }) => {
    const getBubbleClass = () => {
        switch(log.role) {
            case 'user': return 'bg-indigo-500 text-white self-end max-w-xl';
            case 'assistant': return 'bg-gray-200 text-gray-800 self-start max-w-xl';
            case 'tool': return 'bg-purple-100 self-center w-full text-xs my-2 max-w-xl';
            default: return 'bg-gray-200';
        }
    };
    
    if (log.role === 'tool') {
        return (
            <div className={`p-2 rounded-lg ${getBubbleClass()} text-purple-800`}>
                <p className="font-semibold">Tool Call Result:</p>
                <pre className="text-xs whitespace-pre-wrap"><code>{log.content}</code></pre>
            </div>
        )
    }

    if (log.role === 'assistant' && log.tool_calls) {
         return (
            <div className={`p-3 rounded-lg ${getBubbleClass()} max-w-lg`}>
                <p className="text-gray-600 italic">Initiating tool call...</p>
                 <div className="bg-purple-100 p-2 rounded-md mt-2">
                    <p className="font-semibold text-purple-800">Function: {log.tool_calls[0].function.name}</p>
                    <p className="text-xs text-purple-700 whitespace-pre-wrap">Arguments: {log.tool_calls[0].function.arguments}</p>
                 </div>
            </div>
        )
    }

    return (
        <div className={`p-3 rounded-lg ${getBubbleClass()} max-w-lg`}>
            <p>{log.content}</p>
        </div>
    );
};

const AudioPlayer = ({ src }: { src: string }) => {
    const [isPlaying, setIsPlaying] = useState(false);
    const audioRef = React.useRef<HTMLAudioElement>(null);

    const togglePlay = () => {
        if (audioRef.current) {
            if (isPlaying) audioRef.current.pause();
            else audioRef.current.play();
            setIsPlaying(!isPlaying);
        }
    };
    
    useEffect(() => {
        const audio = audioRef.current;
        const handleEnd = () => setIsPlaying(false);
        if(audio) audio.addEventListener('ended', handleEnd);
        return () => { if (audio) audio.removeEventListener('ended', handleEnd) }
    }, []);

    return (
        <div className="flex items-center gap-4 bg-gray-100 p-3 rounded-lg">
            <audio ref={audioRef} src={src} preload="metadata" />
            <button onClick={togglePlay} className="text-indigo-600 hover:text-indigo-800">
                {isPlaying ? <PauseCircle size={28} /> : <PlayCircle size={28} />}
            </button>
            <div className="text-sm text-gray-600">Call Recording</div>
        </div>
    );
}

const SessionModal = ({ sessionId, onClose }: { sessionId: string, onClose: () => void }) => {
    const [sessionData, setSessionData] = useState<SessionData | null>(null);
    const [isLoading, setIsLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const chatContainerRef = React.useRef<HTMLDivElement>(null);

    useEffect(() => {
        const fetch = async () => {
            try {
                setIsLoading(true);
                const data = await api.fetchSessionData(sessionId);
                setSessionData(data);
                setError(null);
            } catch (err) {
                setError("Failed to load session data.");
                console.error(err);
            } finally {
                setIsLoading(false);
            }
        };
        if(sessionId) fetch();
    }, [sessionId]);

    useEffect(() => {
        if(chatContainerRef.current){
            chatContainerRef.current.scrollTop = chatContainerRef.current.scrollHeight;
        }
    }, [sessionData]);

    return (
        <div className="fixed inset-0 bg-black bg-opacity-60 flex justify-center items-center z-50 p-4">
            <div className="bg-white rounded-lg shadow-2xl w-full max-w-6xl h-full max-h-[90vh] flex flex-col relative text-gray-800">
                <button onClick={onClose} className="absolute top-3 right-3 text-gray-500 hover:text-gray-800 transition-colors">
                    <X size={24} />
                </button>
                <div className="p-4 border-b border-gray-200">
                    <h2 className="text-xl font-bold">Call Session Details</h2>
                    {sessionData && <p className="text-sm text-gray-500">Session ID: {sessionData.session_id}</p>}
                </div>

                {isLoading && <div className="flex-grow flex justify-center items-center"><Loader2 className="animate-spin h-10 w-10 text-indigo-500" /></div>}
                {error && <div className="flex-grow flex justify-center items-center text-red-500">{error}</div>}

                {sessionData && (
                    <div className="flex-grow flex flex-col md:flex-row overflow-hidden">
                        <div className="w-full md:w-3/5 flex flex-col p-4 border-r-0 md:border-r border-gray-200">
                            <h3 className="text-lg font-semibold mb-2">Conversation Log</h3>
                            <div ref={chatContainerRef} className="w-full flex-grow bg-gray-50 rounded-lg p-4 space-y-4 overflow-y-auto mb-4">
                                {sessionData.chat_logs.map((log, index) => <ChatMessage key={index} log={log} />)}
                            </div>
                            <AudioPlayer src={sessionData.audio_url} />
                        </div>
                        
                        <div className="w-full md:w-2/5 p-4 overflow-y-auto">
                            <h3 className="text-lg font-semibold mb-3">Insights</h3>
                            <div className="space-y-4">
                               <div className="bg-gray-50 p-3 rounded-lg"><p className="text-sm text-gray-500">Customer</p><p className="font-semibold">{sessionData.customer_name}</p></div>
                               <div className="bg-gray-50 p-3 rounded-lg"><p className="text-sm text-gray-500">Booking Status</p><p className="font-semibold text-indigo-600">{sessionData.booking_status}</p></div>
                                <div className="bg-gray-50 p-3 rounded-lg"><p className="text-sm text-gray-500">Total Cost</p><p className="font-semibold text-green-600">${sessionData.insights.total_cost.USD.toFixed(2)} (₹{sessionData.insights.total_cost.INR.toFixed(2)})</p></div>
                                 <div className="bg-gray-50 p-3 rounded-lg"><p className="text-sm text-gray-500">LLM Usage</p><p className="font-semibold">{sessionData.insights.llm_usage.prompt_tokens + sessionData.insights.llm_usage.completion_tokens} tokens</p><p className="text-xs text-gray-500">Prompt: {sessionData.insights.llm_usage.prompt_tokens}, Completion: {sessionData.insights.llm_usage.completion_tokens}</p></div>
                                {sessionData.insights.function_calls.length > 0 && (
                                    <div className="bg-gray-50 p-3 rounded-lg"><p className="text-sm text-gray-500 mb-1">Function Calls</p>
                                        {sessionData.insights.function_calls.map((call, i) => (
                                             <div key={i} className="text-xs bg-purple-100 p-2 rounded"><p className="font-semibold text-purple-800">{call.function_name}</p><pre className="whitespace-pre-wrap text-purple-700"><code>{JSON.stringify(call.arguments, null, 2)}</code></pre></div>
                                        ))}
                                    </div>
                                )}
                            </div>
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
};


// --- Main Dashboard Component ---
const Dashboard = () => {
    const [collections, setCollections] = useState<Collection[]>([]);
    const [selectedCollection, setSelectedCollection] = useState<string>("");
    const [limit, setLimit] = useState<number>(15);
    const [summary, setSummary] = useState<CampaignSummary | null>(null);
    const [calls, setCalls] = useState<CallDetail[]>([]);
    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [selectedSessionId, setSelectedSessionId] = useState<string | null>(null);

    useEffect(() => {
        const loadCollections = async () => {
            setIsLoading(true);
            try {
                const fetchedCollections = await api.fetchCollections();
                setCollections(fetchedCollections);
                if (fetchedCollections.length > 0) setSelectedCollection(fetchedCollections[0].name);
            } catch (err) {
                setError("Failed to fetch collections. Please check your network connection and API status.");
                console.error(err);
            } finally {
                setIsLoading(false);
            }
        };
        loadCollections();
    }, []);

    useEffect(() => {
        if (!selectedCollection) return;
        const loadCampaignData = async () => {
            setIsLoading(true);
            setError(null);
            try {
                const [summaryData, callData] = await Promise.all([
                    api.fetchCampaignSummary(selectedCollection, limit),
                    api.fetchCallDetails(selectedCollection, limit)
                ]);
                setSummary(summaryData);
                setCalls(callData);
            } catch (err) {
                setError(`Failed to load data for ${selectedCollection}.`);
                console.error(err);
                setSummary(null); setCalls([]);
            } finally {
                setIsLoading(false);
            }
        };
        loadCampaignData();
    }, [selectedCollection, limit]);
    
    const callingStatusData = summary ? Object.entries(summary.calling_status_counts).map(([name, value]) => ({ name, value })) : [];
    const bookingStatusData = summary ? Object.entries(summary.booking_status_counts).map(([name, value]) => ({ name, value })) : [];

    return (
        <div className="bg-gray-50 min-h-screen text-gray-800 p-4 sm:p-6 lg:p-8">
            <div className="max-w-7xl mx-auto">
                <div className="flex flex-col md:flex-row justify-between md:items-center mb-8 gap-4">
                    <h1 className="text-3xl font-bold text-gray-900">Campaign Analytics</h1>
                    <div className="flex flex-col sm:flex-row items-center gap-4">
                        <div>
                            <label htmlFor="limit" className="text-sm font-medium text-gray-600 mr-2">Limit</label>
                            <input
                                id="limit"
                                type="number"
                                value={limit}
                                onChange={(e) => setLimit(Number(e.target.value))}
                                className="w-24 rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm p-2"
                                placeholder="Limit"
                            />
                        </div>
                        <CollectionDropdown collections={collections} selected={selectedCollection} onSelect={setSelectedCollection} isLoading={isLoading && !summary} />
                    </div>
                </div>

                {isLoading && !summary && <div className="flex justify-center items-center py-20"><Loader2 className="h-12 w-12 animate-spin text-indigo-600" /></div>}
                {error && <div className="bg-red-100 text-red-700 p-4 rounded-lg text-center">{error}</div>}

                {summary && (
                    <div className="space-y-8">
                         <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-6">
                             <SummaryCard title="Total Calls" value={summary.call_summary.total_calls.toLocaleString()} icon={<Phone size={24}/>} />
                             <SummaryCard title="Completed Calls" value={summary.calling_status_counts.completed || 0} icon={<CheckCircle size={24}/>} />
                             <SummaryCard title="Not Answered Calls" value={summary.calling_status_counts.no_answer || 0} icon={<AlertTriangle size={24}/>} />
                             <SummaryCard title="Avg. Call Duration" value={summary.call_summary.average_call_duration_formatted} icon={<Clock size={24}/>} />
                             <SummaryCard title="Total Campaign Duration" value={summary.call_summary.total_call_duration_formatted} icon={<Clock size={24}/>} />
                             {/* <SummaryCard title="Total Cost (INR)" value={`₹${summary.call_summary.total_cost_inr.toFixed(2)}`} icon={<DollarSign size={24}/>} /> */}

                         </div>
                         <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-2 gap-6">

                            <CostBreakdownCard costUsd={summary.call_summary.total_cost_usd} />
                            <CallingStatusCounts counts={summary.calling_status_counts} />
                        </div>
                         
                         <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                            <CustomPieChart data={callingStatusData} title="Calling Status" />
                            <CustomBarChart data={bookingStatusData} title="Booking Status" nameKey="name" dataKey="value" />
                         </div>

                         <div>
                            <h2 className="text-2xl font-bold mb-4">Call Details</h2>
                            <CallDetailsTable calls={calls} onSelectCall={setSelectedSessionId}/>
                         </div>
                    </div>
                )}

                 {selectedSessionId && <SessionModal sessionId={selectedSessionId} onClose={() => setSelectedSessionId(null)} />}
            </div>
        </div>
    );
};

export default Dashboard;
